import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, MessageCircle, CheckCircle } from 'lucide-react';

interface FormData {
  name: string;
  phone: string;
  email: string;
  comment: string;
}

const initialFormData: FormData = {
  name: '',
  phone: '',
  email: '',
  comment: ''
};

const ContactInfo: React.FC = () => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [formSuccess, setFormSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setFormSuccess(true);
    setFormData(initialFormData);
    setTimeout(() => setFormSuccess(false), 5000);
  };

  const contactItems = [
    {
      icon: MapPin,
      title: 'Адрес',
      content: (
        <>
          Республика Казахстан, город Павлодар,<br />
          ул. Исы Байзакова, 41, 3 этаж
        </>
      )
    },
    {
      icon: Phone,
      title: 'Телефоны',
      content: (
        <>
          <a href="tel:+77019823126" className="hover:text-primary-500 transition-colors">+7 701 982 31 26</a><br />
          <a href="tel:+77054230935" className="hover:text-primary-500 transition-colors">+7 705 423 09 35</a>
        </>
      )
    },
    {
      icon: Mail,
      title: 'Email',
      content: <a href="mailto:info@hasantravel.kz" className="hover:text-primary-500 transition-colors">info@hasantravel.kz</a>
    },
    {
      icon: Clock,
      title: 'График работы',
      content: (
        <>
          Понедельник - Пятница: 11:00 - 19:00<br />
          Суббота: 12:00 - 16:00<br />
          Воскресенье: Выходной
        </>
      )
    }
  ];

  return (
    <section id="contacts" className="py-20 bg-neutral-50">
      <div className="container-custom">
        <div className="text-center mb-14">
          <div className="inline-block bg-primary-500/10 text-primary-500 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            Контакты
          </div>
          <h2 className="section-title mx-auto">Свяжитесь с нами</h2>
          <p className="text-neutral-500 mt-4 max-w-xl mx-auto">
            Если у вас есть вопросы или вы хотите забронировать тур, свяжитесь с нами любым удобным способом
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="space-y-6 mb-8">
              {contactItems.map((item, index) => (
                <div key={index} className="flex group">
                  <div className="w-14 h-14 rounded-2xl bg-primary-500/10 flex items-center justify-center mr-5 flex-shrink-0 group-hover:bg-primary-500 transition-colors duration-300">
                    <item.icon className="w-6 h-6 text-primary-500 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-neutral-800 mb-1">{item.title}</h3>
                    <p className="text-neutral-600 leading-relaxed">{item.content}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Quick contact buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              <a
                href="https://wa.me/77019823126"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-green-500 text-white rounded-xl px-5 py-3.5 font-medium hover:bg-green-600 transition-all shadow-lg shadow-green-500/20 hover:shadow-xl hover:shadow-green-500/30"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </a>
              <a
                href="https://t.me/hasantravel"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-blue-500 text-white rounded-xl px-5 py-3.5 font-medium hover:bg-blue-600 transition-all shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/30"
              >
                <Send className="w-5 h-5" />
                Telegram
              </a>
            </div>

            {/* Social Media */}
            <div>
              <h3 className="text-sm font-semibold text-neutral-400 uppercase tracking-wider mb-4">Мы в социальных сетях</h3>
              <div className="flex gap-3">
                <a href="#" className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 text-white flex items-center justify-center hover:shadow-lg hover:shadow-pink-500/30 transition-all hover:-translate-y-0.5">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a href="#" className="w-12 h-12 rounded-xl bg-blue-600 text-white flex items-center justify-center hover:shadow-lg hover:shadow-blue-600/30 transition-all hover:-translate-y-0.5">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a href="#" className="w-12 h-12 rounded-xl bg-red-600 text-white flex items-center justify-center hover:shadow-lg hover:shadow-red-600/30 transition-all hover:-translate-y-0.5">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                  </svg>
                </a>
                <a href="#" className="w-12 h-12 rounded-xl bg-sky-500 text-white flex items-center justify-center hover:shadow-lg hover:shadow-sky-500/30 transition-all hover:-translate-y-0.5">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <div className="bg-white shadow-xl rounded-2xl p-8 border border-neutral-100">
              <h2 className="text-2xl font-bold text-neutral-800 mb-2">Заказать обратный звонок</h2>
              <p className="text-neutral-500 text-sm mb-6">Мы перезвоним вам в течение 15 минут</p>

              {formSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-5 py-4 rounded-xl mb-6 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                  <span>Спасибо! Ваша заявка успешно отправлена. Мы свяжемся с вами в ближайшее время.</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-neutral-600 mb-1.5">
                    Ваше имя *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="Введите ваше имя"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-neutral-600 mb-1.5">
                    Телефон *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="+7 (___) ___ __ __"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-neutral-600 mb-1.5">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="comment" className="block text-sm font-medium text-neutral-600 mb-1.5">
                    Комментарий
                  </label>
                  <textarea
                    id="comment"
                    name="comment"
                    value={formData.comment}
                    onChange={handleChange}
                    rows={4}
                    className="input-field resize-none"
                    placeholder="Куда хотите поехать, на какие даты..."
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-secondary w-full flex items-center justify-center gap-2 py-4"
                >
                  <Send className="w-5 h-5" />
                  Отправить заявку
                </button>

                <p className="text-xs text-neutral-400 text-center">
                  Нажимая кнопку, вы соглашаетесь с политикой конфиденциальности и даете согласие на обработку персональных данных.
                </p>
              </form>
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="mt-14 rounded-2xl overflow-hidden shadow-xl h-96 border border-neutral-200">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2428.5!2d76.9548!3d52.2855!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x42f83e3a00000001%3A0x1!2sIsa%20Baizakova%20St%2041%2C%20Pavlodar%2C%20Kazakhstan!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Карта расположения офиса HASAN TRAVEL"
          />
        </div>
      </div>
    </section>
  );
};

export default ContactInfo;
